import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { userService } from "../../_services";

const AddressData = {
    Country: ["United States"],
    State: [
        "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
    ]
};

const formSchema = z.object({
    Fname: z.string().min(1, "First Name is required"),
    Lname: z.string().min(1, "Last Name is required"),
    Address1: z.string().min(1, "Address is required"),
    Address2: z.string().optional(),
    State: z.string().min(1, "State is required"),
    City: z.string().min(1, "City is required"),
    Zip: z.string().regex(/^\d{5}$/, "Enter a valid 5-digit Zip Code"),
    Email: z.string().email("Enter a valid Email"),
    PhoneNo: z.string().min(10, "Enter a valid Phone Number"),
    GroupNumber: z.string().min(1, "Group Number is required"),
});

const AddressForm = ({ onSubmit }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [groupNumbers, setGroupNumbers] = useState([]);

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            Fname: "",
            Lname: "",
            Address1: "",
            Address2: "",
            State: "",
            City: "",
            Zip: "",
            Email: "",
            PhoneNo: "",
            GroupNumber: "",
        },
    });

    const fetchGroupNumbers = async () => {
        try {
            let Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
            const response = await userService.GetUserGroups(Usermeta_Data.split(':')[0], Usermeta_Data.split(':')[1]);
            if (response && response.length > 0) {
                setGroupNumbers(response);
                return response;
            }
            return [];
        } catch (error) {
            console.error("Error fetching group numbers:", error);
            return [];
        }
    };

    useEffect(() => {
        const fetchUserData = async () => {
            setLoading(true);
            setError("");
            try {
                let Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
                const userData = await userService.GetUserAccountInfo(Usermeta_Data.split(':')[0], Usermeta_Data.split(':')[1]);
                if (userData && userData.length) {
                    const gnData = await fetchGroupNumbers();
                    const primaryGroup = gnData.find(groupno => groupno.isPrimary);
                    
                    form.reset({
                        Fname: userData[0].fname || "",
                        Lname: userData[0].lname || "",
                        Address1: userData[0].address1 || "",
                        Address2: userData[0].address2 || "",
                        State: userData[0].stateName || "",
                        City: userData[0].cityName || "",
                        Zip: userData[0].zipCode || "",
                        Email: userData[0].email || "",
                        PhoneNo: userData[0].phone || "",
                        GroupNumber: primaryGroup?.groupnumber || ''
                    });
                }
            } catch (error) {
                setError("Failed to fetch user data. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, []);

    const handleSubmit = (values) => {
        onSubmit(values);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
        );
    }

    return (
        <div className="py-4">
            {error && (
                <Alert variant="destructive" className="mb-4">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                    {/* Group Number - First Field */}
                    <FormField
                        control={form.control}
                        name="GroupNumber"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Group Number</FormLabel>
                                <Select onValueChange={field.onChange} value={field.value}>
                                    <FormControl>
                                        <SelectTrigger className="h-12">
                                            <SelectValue placeholder="Select Group Number" />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        {groupNumbers.map((group, index) => (
                                            <SelectItem key={index} value={group.groupnumber}>
                                                {group.groupnumber}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {/* Name Fields */}
                    <div className="grid grid-cols-2 gap-4">
                        <FormField
                            control={form.control}
                            name="Fname"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>First Name</FormLabel>
                                    <FormControl>
                                        <Input placeholder="John" {...field} className="h-12" />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="Lname"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Last Name</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Doe" {...field} className="h-12" />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>

                    {/* Address Fields */}
                    <FormField
                        control={form.control}
                        name="Address1"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Address Line 1</FormLabel>
                                <FormControl>
                                    <Input placeholder="123 Main St" {...field} className="h-12" />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="Address2"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Address Line 2 (Optional)</FormLabel>
                                <FormControl>
                                    <Input placeholder="Apt 4B" {...field} className="h-12" />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {/* State - Before City and Zip */}
                    <FormField
                        control={form.control}
                        name="State"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>State</FormLabel>
                                <Select onValueChange={field.onChange} value={field.value}>
                                    <FormControl>
                                        <SelectTrigger className="h-12">
                                            <SelectValue placeholder="Select State" />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        {AddressData.State.map((state, index) => (
                                            <SelectItem key={index} value={state}>
                                                {state}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {/* City and Zip */}
                    <div className="grid grid-cols-2 gap-4">
                        <FormField
                            control={form.control}
                            name="City"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>City</FormLabel>
                                    <FormControl>
                                        <Input placeholder="New York" {...field} className="h-12" />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="Zip"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Zip Code</FormLabel>
                                    <FormControl>
                                        <Input placeholder="10001" {...field} className="h-12" maxLength={5} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>

                    {/* Contact Fields */}
                    <FormField
                        control={form.control}
                        name="Email"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Email</FormLabel>
                                <FormControl>
                                    <Input type="email" placeholder="john@example.com" {...field} className="h-12" />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="PhoneNo"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Phone Number</FormLabel>
                                <FormControl>
                                    <Input type="tel" placeholder="(555) 123-4567" {...field} className="h-12" />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <Button 
                        type="submit" 
                        className="w-full h-12" 
                        variant="secondary"
                        size="lg"
                        disabled={form.formState.isSubmitting}
                    >
                        {form.formState.isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Continue to Payment
                    </Button>
                </form>
            </Form>
        </div>
    );
};

export default AddressForm;
